/**
 * useFilteredVideoFeed
 *
 * Wraps the raw video feed hooks and applies the admin access-control rules:
 *  - View whitelist/blacklist
 *  - Content filters (keyword, hashtag, pubkey, domain)
 *
 * Components should use these instead of the raw feed hooks so admin rules
 * are respected globally.
 */
import { useMemo } from 'react';
import { useAdminConfig } from '@/hooks/useAdminConfig';
import { canView } from '@/hooks/useAdminConfig';
import { useVideoFeed, useShortsFeed } from '@/hooks/useVideoFeed';
import { extractTags, extractVideoUrl, extractTitle } from '@/hooks/useVideoFeed';
import type { VideoEvent } from '@/hooks/useVideoFeed';
import type { ContentFilter } from '@/hooks/useAdminConfig';
import type { NostrEvent } from '@nostrify/nostrify';

function applyContentFilter(event: NostrEvent, filter: ContentFilter): boolean {
  if (!filter.enabled) return false;

  const val = filter.value.toLowerCase();

  switch (filter.type) {
    case 'keyword': {
      const title = extractTitle(event).toLowerCase();
      const content = event.content.toLowerCase();
      return title.includes(val) || content.includes(val);
    }
    case 'hashtag': {
      const tags = extractTags(event).map(t => t.toLowerCase());
      return tags.includes(val.replace(/^#/, ''));
    }
    case 'pubkey': {
      return event.pubkey === val || event.pubkey.startsWith(val);
    }
    case 'domain': {
      const url = extractVideoUrl(event);
      if (!url) return false;
      try {
        return new URL(url).hostname.endsWith(val);
      } catch {
        return false;
      }
    }
    default:
      return false;
  }
}

/**
 * Returns true if the event should be hidden by any enabled "hide" filter.
 */
function isFilteredOut(event: NostrEvent, filters: ContentFilter[]): boolean {
  return filters
    .filter(f => f.enabled && f.action === 'hide')
    .some(f => applyContentFilter(event, f));
}

function filterEvents(events: VideoEvent[], adminConfig: ReturnType<typeof useAdminConfig>['config']): VideoEvent[] {
  return events.filter(event => {
    // 1. View access control
    if (!canView(event.pubkey, adminConfig)) return false;
    // 2. Content filters
    if (isFilteredOut(event, adminConfig.contentFilters)) return false;
    return true;
  });
}

export function useFilteredVideoFeed(tag?: string) {
  const { config: adminConfig } = useAdminConfig();
  const raw = useVideoFeed(tag);

  const filtered = useMemo(() => {
    if (!raw.data) return raw.data;
    return filterEvents(raw.data, adminConfig);
  }, [raw.data, adminConfig]);

  return { ...raw, data: filtered };
}

export function useFilteredShortsFeed() {
  const { config: adminConfig } = useAdminConfig();
  const raw = useShortsFeed();

  const filtered = useMemo(() => {
    if (!raw.data) return raw.data;
    return filterEvents(raw.data, adminConfig);
  }, [raw.data, adminConfig]);

  return { ...raw, data: filtered };
}
